export const ROLES = {
  ATC: {
    access: import.meta.env.VITE_ATC_ACCESS,
    user: import.meta.env.VITE_ATC_USER,
    admin: import.meta.env.VITE_ATC_ADMIN,
  },
  FLEET: {
    access: import.meta.env.VITE_FLEET_ACCESS,
    admin: import.meta.env.VITE_FLEET_ADMIN,
  },
  SCHEDULER: {
    admin: import.meta.env.VITE_SCHEDULER_ADMIN,
  },
  INVENTORY: {
    admin: import.meta.env.VITE_INVENTORY_ADMIN,
    leader: import.meta.env.VITE_INVENTORY_LEADER,
    access: import.meta.env.VITE_INVENTORY_ACCESS,
  },
  BUSINESS: {
    admin: import.meta.env.VITE_BUSINESS_ADMIN,
    /** Rol tecnico: acceso a /tech-checks. Se asigna a mano en Keycloak. */
    it: import.meta.env.VITE_BUSINESS_IT,
    dispatcher:
      import.meta.env.VITE_BUSINESS_DISPATCHER,
    supervisorExternalPickup: import.meta.env.VITE_BUSINESS_SUPERVISOR_EXTERNAL_PICKUP,
    supervisor: import.meta.env.VITE_BUSINESS_SUPERVISOR,
    operator: import.meta.env.VITE_BUSINESS_OPERATOR,
    viewer: import.meta.env.VITE_BUSINESS_VIEWER,
    externalPickupViewer: import.meta.env.VITE_BUSINESS_EXTERNAL_PICKUP_VIEWER,
    /** Usuario de comercio con gestión de sus envíos. */
    commerceCrud: import.meta.env.VITE_BUSINESS_COMMERCE_CRUD,
    /** Usuario de comercio de solo lectura. */
    commerceRead: import.meta.env.VITE_BUSINESS_COMMERCE_READ,
    /** Usuario de comercio con gestión y, además, reporte de visitas. */
    commerceReports: import.meta.env.VITE_BUSINESS_COMMERCE_REPORTS,
    /** Agregado al rol de comercio: sube y borra el material de apoyo que ven todos los comercios. */
    commerceSupportMaterial:
      import.meta.env.VITE_BUSINESS_COMMERCE_SUPPORT_MATERIAL ??
      'business:commerce-support-material',
    /** Operador externo con acceso total a la sección */
    externalOperatorSupervisor: import.meta.env.VITE_BUSINESS_EXTERNAL_OPERATOR_SUPERVISOR,
    /** Operador externo cuya flota/operador logístico es propio de la empresa. */
    externalOperatorAffiliate: import.meta.env.VITE_BUSINESS_EXTERNAL_OPERATOR_AFFILIATE,
    /** Operador externo tercero, sin flota propia de la empresa. */
    externalOperatorIndependent: import.meta.env.VITE_BUSINESS_EXTERNAL_OPERATOR_INDEPENDENT,
  },
} as const;

/**
 * ID del operador logístico interno (soydeliverycentral). No es un operador
 * externo real: un usuario cuyo scope de operador lo incluya no debe poder
 * entrar a /business-external-operators aunque tenga los roles.
 */
export const EXCLUDED_EXTERNAL_OPERATOR_ID = 'a667974d-6dbb-433a-9649-0df69a6de4d3';

/**
 * Roles exclusivos de usuarios de tipo comercio. Son excluyentes con los roles
 * de operador: un usuario tiene uno de estos dos, o roles de operador, nunca
 * ambos.
 */
export const COMMERCE_ROLE_NAMES: readonly string[] = [
  ROLES.BUSINESS.commerceCrud,
  ROLES.BUSINESS.commerceRead,
  ROLES.BUSINESS.commerceReports,
  // Si falta la var de entorno el valor llega undefined y el chequeo de rol
  // fallaría en silencio: mejor dejar la lista vacía y que se note.
].filter(Boolean);

/**
 * Qué puede hacer cada rol de comercio dentro de /business-commerces:
 *  - read:    ver el listado, ver un pedido y sacar etiquetas.
 *  - crud:    además crear, importar, editar y eliminar pedidos.
 *  - reports: lo mismo que crud, más el reporte de visitas de sus comercios.
 */
export const COMMERCE_ROLE_GROUPS = {
  /** Acceso a la sección. */
  all: COMMERCE_ROLE_NAMES,
  /** Alta, edición, importación y baja de pedidos. */
  write: [
    ROLES.BUSINESS.commerceCrud,
    ROLES.BUSINESS.commerceReports,
  ].filter(Boolean) as readonly string[],
  /** Exportar el reporte de visitas. */
  reports: [ROLES.BUSINESS.commerceReports].filter(
    Boolean
  ) as readonly string[],
  /** Subir y borrar material de apoyo. */
  supportMaterial: [
    ROLES.BUSINESS.commerceSupportMaterial,
    ROLES.BUSINESS.admin,
  ].filter(Boolean) as readonly string[],
} as const;

/**
 * Roles exclusivos de usuarios de tipo operador logístico externo. Igual que
 * los de comercio, son excluyentes con los roles de operador interno.
 */
export const EXTERNAL_OPERATOR_ROLE_NAMES: readonly string[] = [
  ROLES.BUSINESS.externalOperatorSupervisor,
  ROLES.BUSINESS.externalOperatorAffiliate,
  ROLES.BUSINESS.externalOperatorIndependent,
].filter(Boolean);

/**
 * Qué puede hacer cada rol de operador externo dentro de
 * /business-external-operators:
 *  - all: acceso a la sección (los 3 roles) — envíos, rutear, imprimir
 *    etiquetas, gestión de rutas, asignar conductor/cancelar visita desde
 *    el detalle de un envío, ver el listado de conductores.
 *  - restricted: además, cambio de estado, enmiendas y asignar operador
 *    logístico a un conductor existente (todos menos independent).
 *  - admin: además, administrar Sucursales/Comercios/Servicios/Zonas/
 *    Horarios ("Gestión Logística") — exclusivo de supervisor.
 */
export const EXTERNAL_OPERATOR_ROLE_GROUPS = {
  /** Acceso a la sección. */
  all: EXTERNAL_OPERATOR_ROLE_NAMES,
  /** Cambio de estado, enmiendas, asignar operador logístico a conductor. */
  restricted: [
    ROLES.BUSINESS.externalOperatorSupervisor,
    ROLES.BUSINESS.externalOperatorAffiliate,
  ].filter(Boolean) as readonly string[],
  /** Sucursales, comercios, servicios, zonas y horarios. */
  admin: [ROLES.BUSINESS.externalOperatorSupervisor].filter(
    Boolean
  ) as readonly string[],
} as const;

export const BUSINESS_ROLE_GROUPS = {
  full: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.supervisor,
    ROLES.BUSINESS.operator,
    ROLES.BUSINESS.dispatcher,
    ROLES.BUSINESS.viewer,
    ROLES.BUSINESS.externalPickupViewer,
    ROLES.BUSINESS.supervisorExternalPickup,
  ],
  management: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.supervisor,
    ROLES.BUSINESS.supervisorExternalPickup,
  ],
  operations: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.supervisor,
    ROLES.BUSINESS.operator,
    ROLES.BUSINESS.supervisorExternalPickup,
  ],
  withoutViewer: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.supervisor,
    ROLES.BUSINESS.operator,
    ROLES.BUSINESS.dispatcher,
    ROLES.BUSINESS.supervisorExternalPickup,
  ],
  withoutDispatcher: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.supervisor,
    ROLES.BUSINESS.operator,
    ROLES.BUSINESS.viewer,
    ROLES.BUSINESS.externalPickupViewer,
    ROLES.BUSINESS.supervisorExternalPickup,
  ],
  adminOrDispatcher: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.dispatcher,
  ],
  adminOrSupervisor: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.supervisor,
  ],
  billing: [
    ROLES.BUSINESS.admin,
    ROLES.BUSINESS.supervisor,
    ROLES.BUSINESS.operator,
  ],
} as const;

/**
 * Permisos sobre envíos compartidos entre el back office (/business), la
 * sección de comercios (/business-commerces) y la de operadores externos
 * (/business-external-operators), que reusan ShippingsTable/ShippingPackagesTable.
 */
export const SHIPPING_PERMISSIONS = {
  /** Editar un envío o sus bultos. */
  edit: [
    ...BUSINESS_ROLE_GROUPS.operations,
    ...COMMERCE_ROLE_GROUPS.write,
    ...EXTERNAL_OPERATOR_ROLE_GROUPS.all,
  ] as readonly string[],
  /** Imprimir y descargar etiquetas: los dos roles de comercio pueden. */
  labels: [
    ...BUSINESS_ROLE_GROUPS.operations,
    ...COMMERCE_ROLE_GROUPS.all,
  ] as readonly string[],
} as const;

type ValueOf<T> = T[keyof T];

export type ROLES_ATC = ValueOf<typeof ROLES.ATC>;
export type ROLES_FLEET = ValueOf<typeof ROLES.FLEET>;
export type ROLES_SCHEDULER = ValueOf<typeof ROLES.SCHEDULER>;
export type ROLES_INVENTORY = ValueOf<typeof ROLES.INVENTORY>;
export type ROLES_BUSINESS = ValueOf<typeof ROLES.BUSINESS>;
export type ROLES =
  | ROLES_ATC
  | ROLES_FLEET
  | ROLES_SCHEDULER
  | ROLES_INVENTORY
  | ROLES_BUSINESS;

export function userHasPermission(
  permissions: readonly string[],
  permission: ROLES | readonly ROLES[]
): boolean {
  const required = Array.isArray(permission) ? permission : [permission];
  return required.some((p) => permissions.includes(p));
}
